import { AiDrawer } from '@aigc/components';
import { useOverlayRef } from '@aigc/overlay-hook';
import { CloseCircleFilled, ReloadOutlined } from '@ant-design/icons';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Alert,
  App,
  Button,
  Card,
  Image,
  Skeleton,
  theme,
  Tooltip,
} from 'antd';
import { useEffect, useState } from 'react';
import { v7 as uuid } from 'uuid';
import fallbackImage from '../../common/fallback.png';
import type { FileInputDto } from '../../file/model';
import { toFileInputDtoList, toFileListFormValues } from '../../file/utils';
import {
  ImageGenerationForm,
  useImageGenerationForm,
  useImageGenerationSubmit,
} from '../../image/components/image-generation-form';
import { DEFAULT_IMAGE_MODEL_CODE } from '../../image/image-llm';
import { useSetReferenceImagePageData } from '../../reference-image/store';
import { getOneTaskQueryOptions } from '../../task/api';
import { TaskStatus } from '../../task/components/task-status';
import type { ImageGenerationTaskDto } from '../../task/model';
import type { ScriptSource } from '../../video/model';
import type {
  ProductAnalysisShotDto,
  ProductScriptFormValues,
  ProductShotImageGenerationParams,
} from '../model';
import {
  appendProductShotImageTask,
  buildProductAnalysisVideoPageData,
  removeProductShotImageTask,
  type ProductShotImageTaskRecord,
} from '../utils';

interface TrackedImageTask extends ProductShotImageTaskRecord {
  query?: UseQueryResult<ImageGenerationTaskDto>;
}

function ShotImageGenerationForm(props: {
  source: ScriptSource | undefined;
  product_name: string;
  product_images: FileInputDto[];
  shot: ProductAnalysisShotDto;
  shot_position: number;
  image_instructions: string;
  initial_params?: ProductShotImageGenerationParams;
  onImageInstructionsChange: (image_instructions: string) => void;
  onParamsChange: (params: ProductShotImageGenerationParams) => void;
  onTaskCreated: (task: ImageGenerationTaskDto) => void;
}) {
  const form = useImageGenerationForm({
    name: `${props.product_name}-镜号${props.shot.shot_index}`,
    prompt: props.image_instructions,
    quantity: 1,
    file_list: props.initial_params
      ? toFileListFormValues(props.initial_params.reference_images)
      : toFileListFormValues(props.product_images),
    aspect_ratio: props.initial_params?.aspect_ratio ?? '9:16',
    model: props.initial_params?.model ?? DEFAULT_IMAGE_MODEL_CODE,
  });
  const generation = useImageGenerationSubmit(form, {
    successMessage: `镜号 ${props.shot.shot_index} 图片生成任务已提交`,
    onTaskCreated: props.onTaskCreated,
    source: props.source,
  });

  useEffect(() => {
    const saveParams = () => {
      const values = form.getValues();

      props.onImageInstructionsChange(values.prompt);
      props.onParamsChange({
        reference_images: toFileInputDtoList(values.file_list),
        aspect_ratio: values.aspect_ratio ?? null,
        model: values.model,
      });
    };

    saveParams();

    return form.subscribe({
      formState: { values: true },
      callback: saveParams,
    });
  }, [form, props]);

  return (
    <ImageGenerationForm
      form={form}
      isPending={generation.isPending}
      onSubmit={generation.submit}
      showName={false}
      showQuantity={false}
      uploaderId={`shotImageUploader-${props.shot_position}`}
      enableAutoPaste={false}
    />
  );
}

function GeneratedImageTask(props: {
  tracked_task: TrackedImageTask;
  onRemove: () => void;
}) {
  const { token } = theme.useToken();
  const { query, initial_task } = props.tracked_task;
  const task = query?.data ?? initial_task;
  let content;

  if (query?.isError) {
    content = (
      <div className="space-y-2">
        <Tooltip title="任务详情加载失败">
          <Image
            preview={false}
            width={96}
            height={96}
            src=""
            fallback={fallbackImage}
          />
        </Tooltip>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          loading={query.isFetching}
          onClick={() => query.refetch()}
        >
          重试加载
        </Button>
      </div>
    );
  } else if (!TaskStatus.isSettled(task.status)) {
    content = (
      <Skeleton.Image active classNames={{ content: '!h-24 !w-24 rounded' }} />
    );
  } else if (TaskStatus.isFailed(task.status)) {
    content = (
      <Tooltip title={task.error || '图片生成失败'}>
        <Image
          preview={false}
          width={96}
          height={96}
          src=""
          fallback={fallbackImage}
        />
      </Tooltip>
    );
  } else {
    const image_urls = task.result?.image_urls ?? [];

    content = image_urls.length ? (
      <Image.PreviewGroup items={image_urls}>
        <Image
          className="rounded object-cover"
          width={96}
          height={96}
          src={image_urls[0]}
          fallback={fallbackImage}
        />
      </Image.PreviewGroup>
    ) : (
      <Tooltip title="图片生成成功，但结果为空">
        <Image
          preview={false}
          width={96}
          height={96}
          src=""
          fallback={fallbackImage}
        />
      </Tooltip>
    );
  }

  return (
    <div className="group relative w-fit">
      {content}
      <button
        type="button"
        className="absolute right-0 top-0 z-10 flex -translate-y-1/2 translate-x-1/2 cursor-pointer items-center justify-center border-0 p-0 opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
        style={{
          color: token.colorTextLabel,
          backgroundColor: token.colorFillTertiary,
          fontSize: token.fontSizeLG,
          lineHeight: 1,
        }}
        aria-label="移除图片任务"
        onClick={props.onRemove}
      >
        <CloseCircleFilled />
      </button>
    </div>
  );
}

export function ProductShotImageDrawer(props: {
  source: ScriptSource | undefined;
  productName: string;
  productImages: FileInputDto[];
  shots: ProductAnalysisShotDto[];
  formValues: ProductScriptFormValues;
  createdTasks: ProductShotImageTaskRecord[];
  imageGenerationParams: Record<number, ProductShotImageGenerationParams>;
  onImageGenerationParamsChange: (
    shot_position: number,
    params: ProductShotImageGenerationParams,
  ) => void;
  onImageInstructionsChange: (
    shot_position: number,
    image_instructions: string,
  ) => void;
  onTaskCreated: (shot_position: number, task: ImageGenerationTaskDto) => void;
  onTaskRemoved: (task_id: string) => void;
}) {
  const { modal } = App.useApp();
  const overlayRef = useOverlayRef<boolean>();
  const navigate = useNavigate();
  const set_reference_image_page_data = useSetReferenceImagePageData(false);
  const set_long_reference_image_page_data = useSetReferenceImagePageData(true);
  const [visible_tasks, set_visible_tasks] = useState(props.createdTasks);
  const task_queries = useQueries({
    queries: visible_tasks.map(({ initial_task }) => ({
      ...getOneTaskQueryOptions<ImageGenerationTaskDto>(initial_task.id),
      initialData: initial_task,
      refetchInterval: (query: { state: { data?: ImageGenerationTaskDto } }) =>
        TaskStatus.isSettled(query.state.data?.status) ? false : 5000,
    })),
  });
  const tracked_tasks = visible_tasks.map<TrackedImageTask>((task, index) => ({
    ...task,
    query: task_queries[index],
  }));

  const successful_images = task_queries.flatMap<FileInputDto>((query) => {
    if (!TaskStatus.isSucceeded(query.data.status)) return [];
    return toFileInputDtoList(
      toFileListFormValues(query.data.result?.image_urls ?? [], 'image/png'),
    );
  });
  const has_pending_images = task_queries.some(
    (query) => !query.isError && !TaskStatus.isSettled(query.data.status),
  );

  const submitVideo = () => {
    const page_data = buildProductAnalysisVideoPageData({
      id: uuid(),
      product_name: props.productName,
      product_images: props.productImages,
      generated_images: successful_images,
      shots: props.shots,
      form_values: props.formValues,
    });

    if (page_data.duration > 15) {
      set_long_reference_image_page_data(page_data);
      navigate({ to: '/video/long/reference-create' });
    } else {
      set_reference_image_page_data(page_data);
      navigate({ to: '/video/reference-create' });
    }

    overlayRef.close(true);
  };

  const handleGenerateVideo = () => {
    if (has_pending_images) {
      modal.confirm({
        title: '仍有图片正在生成',
        content: '继续生成视频将忽略尚未完成的图片，是否继续？',
        okText: '继续生成',
        cancelText: '继续等待',
        onOk: submitVideo,
      });
      return;
    }

    submitVideo();
  };

  return (
    <AiDrawer
      title="生成分镜图片"
      size="50%"
      classNames={{ footer: 'flex justify-end' }}
      onClose={() => overlayRef.close()}
      footer={
        <Button variant="solid" color="primary" onClick={handleGenerateVideo}>
          生成视频
        </Button>
      }
    >
      <Alert
        classNames={{ root: '!mb-4' }}
        showIcon
        type="info"
        title="建议生成 1–3 张关键分镜，优先选择镜号1、重点展示或易出错镜头；无需每镜生成，参考图过多会增加生成限制。"
      ></Alert>
      <div className="space-y-6">
        {props.shots.map((shot, shot_position) => {
          const form_value = props.formValues.shots[shot_position];
          const shot_tasks = tracked_tasks.filter(
            (task) => task.shot_position === shot_position,
          );

          return (
            <Card
              key={`${shot.shot_index}-${shot_position}`}
              title={`镜号 ${shot.shot_index}`}
            >
              <ShotImageGenerationForm
                source={props.source}
                product_name={props.productName}
                product_images={props.productImages}
                shot={shot}
                shot_position={shot_position}
                image_instructions={form_value?.image_instructions ?? ''}
                initial_params={props.imageGenerationParams[shot_position]}
                onImageInstructionsChange={(image_instructions) =>
                  props.onImageInstructionsChange(
                    shot_position,
                    image_instructions,
                  )
                }
                onParamsChange={(params) =>
                  props.onImageGenerationParamsChange(shot_position, params)
                }
                onTaskCreated={(task) => {
                  const record = { shot_position, initial_task: task };

                  set_visible_tasks((current) =>
                    appendProductShotImageTask(current, record),
                  );
                  props.onTaskCreated(shot_position, task);
                }}
              />
              {!!shot_tasks.length && (
                <div className="mt-4 flex flex-wrap gap-3 border-t border-primary-200 pt-4">
                  {shot_tasks.map((tracked_task) => (
                    <GeneratedImageTask
                      key={tracked_task.initial_task.id}
                      tracked_task={tracked_task}
                      onRemove={() => {
                        const task_id = tracked_task.initial_task.id;

                        set_visible_tasks((current) =>
                          removeProductShotImageTask(current, task_id),
                        );
                        props.onTaskRemoved(task_id);
                      }}
                    />
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </AiDrawer>
  );
}
